/**
 * audio-watermark-detector.js — deterministic AI-origin signal scanner
 *
 * Reads the first ~256 KB of the file (no decode) and scans for known
 * AI-watermark / metadata fingerprints. Plus an optional spectral
 * fingerprint check on a decoded AudioBuffer.
 *
 * Why deterministic checks alongside heuristics: when an AI generator
 * leaves an explicit fingerprint (Stable Audio metadata flag, C2PA
 * Content Credentials, an ID3 tag naming Suno/Udio/MusicGen, etc.) we
 * can give a 100 % confident "Confirmed AI" verdict — which heuristic
 * scoring (ai-detector Phase A) can never deliver. False positives are
 * essentially zero because the patterns are explicit identifiers, not
 * statistical guesses.
 *
 * Public API:
 *
 *   const wm = await window.AudioWatermarkDetector.scan(file);
 *   //  → { detected: bool, source: 'stable_audio'|'suno'|'udio'|...,
 *   //      kind: 'metadata'|'c2pa'|'spectral_fingerprint',
 *   //      confidence: 1.0,    // deterministic checks
 *   //      evidence: '<short human-readable evidence>' }
 *
 *   const sf = window.AudioWatermarkDetector.spectralFingerprint(audioBuffer);
 *   //  → { fingerprint: '22050_cutoff'|'16000_cutoff'|null, confidence: 0..1 }
 *
 * Loaded by /ai-detector. Same loader pattern as stem-separator.js.
 */
(function(){
'use strict';

if (window.AudioWatermarkDetector && !window.AudioWatermarkDetector.__isStub) return;

/* ─────────────────────────────────────────────────────────────
   Metadata patterns — case-insensitive byte scan in first chunks.
   Each entry: { source label, kind, regex pattern, evidence template }.
   Patterns are intentionally narrow to avoid false positives — they
   match identifier strings AI tools embed in WAV LIST chunks, ID3v2
   TXXX/TIT2 frames, FLAC Vorbis comments, OGG comment headers.
   ────────────────────────────────────────────────────────────── */
var METADATA_PATTERNS = [
    // Stable Audio (Stability AI)
    { source: 'stable_audio',  kind: 'metadata', re: /Stable[\s_-]?Audio/i,                    evidence: 'Stable Audio metadata tag' },
    { source: 'stable_audio',  kind: 'metadata', re: /stability[\s_-]?ai/i,                    evidence: 'Stability AI metadata tag' },

    // Suno
    { source: 'suno',          kind: 'metadata', re: /\bsuno\b(?:[\s_-]?ai)?/i,                evidence: 'Suno metadata tag' },
    { source: 'suno',          kind: 'metadata', re: /generated[\s_-]?by[\s_-]?suno/i,         evidence: 'Suno "Generated by" tag' },
    { source: 'suno',          kind: 'metadata', re: /chirp[\s_-]?(?:v\d|audio)/i,             evidence: 'Suno Chirp engine tag' },

    // Udio
    { source: 'udio',          kind: 'metadata', re: /\budio\b/i,                              evidence: 'Udio metadata tag' },
    { source: 'udio',          kind: 'metadata', re: /generated[\s_-]?by[\s_-]?udio/i,         evidence: 'Udio "Generated by" tag' },

    // ElevenLabs
    { source: 'elevenlabs',    kind: 'metadata', re: /eleven[\s_-]?labs/i,                     evidence: 'ElevenLabs metadata tag' },
    { source: 'elevenlabs',    kind: 'metadata', re: /\bxi[\s_-]?labs\b/i,                     evidence: 'XI Labs (ElevenLabs) tag' },

    // Meta (MusicGen / AudioCraft / AudioGen)
    { source: 'musicgen',      kind: 'metadata', re: /music[\s_-]?gen/i,                       evidence: 'MusicGen metadata tag' },
    { source: 'musicgen',      kind: 'metadata', re: /audio[\s_-]?craft/i,                     evidence: 'AudioCraft metadata tag' },
    { source: 'musicgen',      kind: 'metadata', re: /\baudio[\s_-]?gen\b/i,                   evidence: 'AudioGen metadata tag' },

    // Riffusion
    { source: 'riffusion',     kind: 'metadata', re: /riffusion/i,                             evidence: 'Riffusion metadata tag' },

    // AudioLDM
    { source: 'audioldm',      kind: 'metadata', re: /audio[\s_-]?ldm[\s_-]?\d?/i,             evidence: 'AudioLDM metadata tag' },

    // AIVA / Boomy / Mubert
    { source: 'aiva',          kind: 'metadata', re: /\baiva\b[\s_-]?(?:ai)?/i,                evidence: 'AIVA metadata tag' },
    { source: 'boomy',         kind: 'metadata', re: /\bboomy\b/i,                             evidence: 'Boomy metadata tag' },
    { source: 'mubert',        kind: 'metadata', re: /\bmubert\b/i,                            evidence: 'Mubert metadata tag' },

    // Soundraw, Beatoven, Loudly, Soundverse
    { source: 'soundraw',      kind: 'metadata', re: /soundraw/i,                              evidence: 'Soundraw metadata tag' },
    { source: 'beatoven',      kind: 'metadata', re: /beatoven/i,                              evidence: 'Beatoven.ai metadata tag' },
    { source: 'loudly',        kind: 'metadata', re: /loudly[\s_-]?ai/i,                       evidence: 'Loudly.com metadata tag' },
    { source: 'soundverse',    kind: 'metadata', re: /soundverse/i,                            evidence: 'Soundverse metadata tag' },

    // Generic AI-origin claim — NOT a specific generator, but a strong
    // hint when it appears with no human creator credit. Lower confidence.
    { source: 'generic_ai',    kind: 'metadata', re: /generated[\s_-]?(?:with[\s_-])?(?:by[\s_-])?(?:artificial[\s_-]?intelligence|ai)/i, evidence: 'Generic AI generation tag', conf: 0.85 },
    { source: 'generic_ai',    kind: 'metadata', re: /\bai[\s_-]?generated\b/i,                evidence: 'Generic "AI Generated" tag', conf: 0.85 },
];

/* ─────────────────────────────────────────────────────────────
   C2PA / Content Credentials — Adobe + OpenAI + Microsoft
   "AI provenance" standard. The manifest is a JUMBF-wrapped CBOR
   blob inserted into media containers. Detect by the magic
   identifiers that always appear when a C2PA manifest is present.
   ────────────────────────────────────────────────────────────── */
var C2PA_MARKERS = [
    'c2pa.assertions',
    'c2pa.actions',
    'c2pa.claim',
    'c2pa_manifest',
    'urn:uuid:c2pa', // C2PA superbox UUID prefix (string-encoded)
    'c2patool',
];
// ⚠️ 'jumbf' was here and is deliberately gone (2026-08-06). JUMBF is a generic
// ISO box format — a file can carry one for reasons that have nothing to do with
// AI provenance, and the bare 4-letter string also turns up inside binary data.
// Every remaining marker names C2PA explicitly, so it cannot be a coincidence.

/* ─────────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────────── */
function readFirstChunk(file, bytes){
    return new Promise(function(resolve, reject){
        var slice = file.slice(0, Math.min(bytes, file.size));
        var fr = new FileReader();
        fr.onload = function(){ resolve(new Uint8Array(fr.result)); };
        fr.onerror = function(){ reject(fr.error || new Error('FileReader failed')); };
        fr.readAsArrayBuffer(slice);
    });
}

function readLastChunk(file, bytes){
    return new Promise(function(resolve, reject){
        if (file.size <= bytes) return readFirstChunk(file, bytes).then(resolve, reject);
        var slice = file.slice(file.size - bytes, file.size);
        var fr = new FileReader();
        fr.onload = function(){ resolve(new Uint8Array(fr.result)); };
        fr.onerror = function(){ reject(fr.error || new Error('FileReader failed')); };
        fr.readAsArrayBuffer(slice);
    });
}

/* Decode bytes as UTF-8 string with replacement for invalid sequences.
   `TextDecoder('utf-8', {fatal:false})` is enough for metadata scanning —
   we only care about ASCII-range identifier matches. */
function decodeUtf8(bytes){
    try {
        return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    } catch (_) {
        // Fallback: byte-to-char for older browsers
        var s = '';
        for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
        return s;
    }
}

/* Match the vendor patterns against PARSED TAG FIELDS, not raw bytes.
 *
 * The same word means different things in different fields, and only the field
 * tells them apart:
 *   TXXX:digitalsourcetype = trainedAlgorithmicMedia  → a declaration
 *   TSSE / COMM / TXXX     = "Suno AI"                → a tool naming itself
 *   TIT2                   = "Suno Na Sangemarmar"    → a Hindi song title
 *
 * Measured 2026-08-06 on 227 verified-human tracks carrying their original
 * tags: the old raw-byte scan produced a 100 %-confidence "Confirmed AI"
 * verdict from the byte sequence "aIVa" inside the compressed audio stream.
 * Field-scoped matching cannot do that — audio frames are not a field.
 */
function scanTagFields(fields){
    var weak = null;
    for (var f = 0; f < fields.length; f++) {
        var fld = fields[f];
        var hay = String(fld.value == null ? '' : fld.value);
        var key = String(fld.key || '');

        // (1) machine-readable declaration — the IPTC term, or our own marker
        if (fld.cls === 'declaration-key') {
            var TR = /trainedalgorithmicmedia|compositewithtrainedalgorithmicmedia/i;
            if (TR.test(hay) || /^(true|1|yes)$/i.test(hay.trim())) {
                return {
                    detected: true, source: 'declared_ai', kind: 'declaration',
                    confidence: 1.0, strength: 'declaration', field: fld.name + (key ? ':' + key : ''),
                    evidence: 'Machine-readable AI declaration (' + (key || fld.name) + ' = "' + hay.slice(0, 40) + '")'
                };
            }
        }

        // (2) vendor self-identification, but only where a TOOL writes
        for (var i = 0; i < METADATA_PATTERNS.length; i++) {
            var p = METADATA_PATTERNS[i];
            var m = p.re.exec(hay) || (key ? p.re.exec(key) : null);
            if (!m) continue;
            var hit = {
                detected: true, source: p.source, kind: p.kind,
                confidence: typeof p.conf === 'number' ? p.conf : 1.0,
                strength: 'vendor_id', field: fld.name + (key ? ':' + key : ''),
                evidence: p.evidence + ' in ' + fld.name + (key ? ':' + key : '') + ' (matched "' + m[0].slice(0, 32) + '")'
            };
            if (fld.cls === 'content') {
                // A person typed this. Remember it as context, keep looking for
                // real evidence — never return it as a detection.
                if (!weak) {
                    weak = {
                        detected: false, source: p.source, kind: 'name_mention',
                        confidence: 0, strength: 'weak', field: fld.name,
                        evidence: 'The name "' + m[0].slice(0, 24) + '" appears in ' + fld.name +
                                  ' — that is the track’s own text, not a generator marker.'
                    };
                }
                continue;
            }
            return hit;
        }
    }
    return weak;   // null, or a name-only mention with detected:false
}

function scanC2PAMarkers(text){
    var lower = text.toLowerCase();
    for (var i = 0; i < C2PA_MARKERS.length; i++) {
        if (lower.indexOf(C2PA_MARKERS[i]) !== -1) {
            return {
                detected: true,
                source: 'c2pa',
                kind: 'c2pa',
                confidence: 1.0,
                evidence: 'C2PA Content Credentials manifest (marker: ' + C2PA_MARKERS[i] + ')'
            };
        }
    }
    return null;
}

/* ─────────────────────────────────────────────────────────────
   Audio bandwidth — where the spectrum stops.
   ⚠️ This USED to claim "cutoff near 11/8/12 kHz ⇒ AI generator", and it was
   WRONG. Measured 2026-08-06 on the labelled corpus:

       fires on verified humans  19/227 = 8.4 %
       fires on Suno            9/141 = 6.4 %

   It fires MORE OFTEN ON PEOPLE than on AI — precision 32 % against a 38 %
   base rate, i.e. firing made a track LESS likely to be generated. The
   steelman ("an AI cap is a brick wall, an MP3 lowpass rolls off") fails
   backwards too: the human wall is STEEPER (median 6.1 dB/kHz vs 3.1).
   A cutoff near 11 kHz simply means the source was 22 kHz or the encoder
   lowpassed it — ordinary for old rips, low bitrates and video audio.

   So the measurement stays (bandwidth is a true, useful fact about a file)
   and the CLAIM is gone: no fingerprint label, no AI verdict, no card.
   ────────────────────────────────────────────────────────────── */
function audioBandwidth(audioBuffer){
    try {
        var sr = audioBuffer.sampleRate;
        var samples = audioBuffer.getChannelData(0);
        var len = samples.length;
        var nyquist = sr / 2;
        if (len < 16384) return { cutoffHz: 0, atNyquist: false, fingerprint: null, confidence: 0 };

        // ── Average the spectrum over FIVE windows spread through the file.
        // A single window (what this used to do) lands wherever it lands: an
        // intro, a breakdown, a pause. Power-averaging across the track is what
        // makes the upper edge a property of the FILE and not of one moment.
        var fftSize = 16384, bandHz = 100;
        var bandsCount = Math.floor(nyquist / bandHz);
        var acc = new Float64Array(bandsCount);
        var spots = [0.15, 0.30, 0.45, 0.60, 0.75], used = 0;
        for (var si = 0; si < spots.length; si++) {
            var off = Math.floor(len * spots[si]);
            if (off + fftSize > len) off = len - fftSize;
            if (off < 0) continue;
            var spectrum = computeMagnitude(samples.slice(off, off + fftSize), fftSize);
            for (var bi = 0; bi < bandsCount; bi++) {
                var loBin = Math.floor(bi * bandHz * fftSize / sr);
                var hiBin = Math.floor((bi + 1) * bandHz * fftSize / sr);
                var sum = 0, n = 0;
                for (var k = loBin; k < hiBin && k < spectrum.length; k++) { sum += spectrum[k]; n++; }
                var mean = sum / Math.max(1, n);
                acc[bi] += mean * mean;                    // power, so quiet windows can't dominate
            }
            used++;
        }
        if (!used) return { cutoffHz: 0, atNyquist: false, fingerprint: null, confidence: 0 };
        var db = new Float64Array(bandsCount);
        for (var bi2 = 0; bi2 < bandsCount; bi2++) db[bi2] = 10 * Math.log10(acc[bi2] / used + 1e-30);

        // 5-band median smoothing — kills single-band spikes (tones, hum harmonics).
        var sm = new Float64Array(bandsCount);
        for (var i = 0; i < bandsCount; i++) {
            var w = [];
            for (var j = Math.max(0, i - 2); j <= Math.min(bandsCount - 1, i + 2); j++) w.push(db[j]);
            w.sort(function (a, b) { return a - b; });
            sm[i] = w[w.length >> 1];
        }
        function med(from, to) {
            var w = [];
            for (var i = Math.max(0, from); i < Math.min(bandsCount, to); i++) w.push(sm[i]);
            if (!w.length) return -300;
            w.sort(function (a, b) { return a - b; });
            return w[w.length >> 1];
        }

        // ── Find the CLIFF: the SHARPEST NARROW STEP in the spectrum — the 600 Hz
        // below a band against the 600 Hz above it, 200 Hz of transition skipped.
        // A lowpass (codec, telephone line, upsample, old rip) is a step; a mix
        // is a slope. Neither "1 % of the loudest band" (before 2026-08-19: it
        // measured where the mix falls 40 dB under the kick — 1.4 kHz for techno,
        // report W_SA #923) nor "the biggest drop between the 3 kHz below and
        // the 3 kHz above" (2026-08-19 → 2026-09-29) tells them apart: on a dark
        // mix the natural slope around 2 kHz makes a bigger ±3 kHz drop than a
        // real 16 kHz codec wall, and a 44.1 kHz stereo MP3 read as «~2.0 kHz».
        // The ai-detector withholds its verdict below a 7 kHz edge, so a slope
        // read as a cliff would withhold it from a perfectly good file.
        //
        // Measured 2026-09-29 on 480 human tracks (_aidtest/bw_estimator_v3.py),
        // real resampler chains as ground truth: a codec wall steps 35–85 dB
        // across 600 Hz, and every original's own MP3 wall (median 17.2 kHz) is
        // found; under 7 kHz exactly 1 original of 480 reads — a netlabel MP3
        // that really stops at 5.3 kHz. Narrowband chains (8 / 11 kHz resample,
        // telephone band): 480 of 480 found. The shipped ±3 kHz rule put 2 of
        // the 480 originals under 7 kHz on a slope, and 4 of 480 files cut at
        // ~7.8 kHz at 6.9 kHz.
        //
        // The search stops at 20 kHz: above it the only wall is the FILE's own
        // Nyquist seen through the listener's sound card (44.1 kHz decoded at
        // 48 kHz), a number that would differ between two visitors.
        var STEP_DB = 20;
        var top = Math.min(bandsCount - 8, Math.floor(20000 / bandHz));
        var cliff = -1, best = -Infinity;
        for (var i2 = 8; i2 <= top; i2++) {
            var step = med(i2 - 6, i2) - med(i2 + 2, i2 + 8);
            if (step > best) { best = step; cliff = i2; }
        }
        if (best < STEP_DB || cliff < 0) {
            // No step anywhere: content runs to the top of what we can see.
            // That ceiling is the DECODE's Nyquist (the listener's sound card),
            // so we report the fact — "full band" — and never a number that
            // would differ between two visitors with the same file.
            return { cutoffHz: nyquist, atNyquist: true, fingerprint: null, confidence: 0, stepDb: best > 0 ? best : 0 };
        }
        // Refine to the −6 dB point of the step, relative to the 600 Hz JUST
        // BELOW it. (Relative to the 3 kHz below, a dark mix sits far above the
        // level at the wall, and the old search stopped on the slope instead.)
        var ref = med(cliff - 6, cliff) - 6;
        var cutoffBand = cliff;
        for (var i3 = Math.max(0, cliff - 6); i3 < Math.min(bandsCount, cliff + 8); i3++) {
            if (sm[i3] < ref) { cutoffBand = i3; break; }
        }
        var cutoffHz = cutoffBand * bandHz;
        // A "cliff" within a hair of Nyquist is the anti-alias filter, not a lowpass.
        if (cutoffHz >= nyquist * 0.95) {
            return { cutoffHz: nyquist, atNyquist: true, fingerprint: null, confidence: 0, stepDb: best };
        }
        return { cutoffHz: cutoffHz, atNyquist: false, fingerprint: null, confidence: 0, stepDb: best };
    } catch (e) {
        console.warn('[audio-watermark-detector] audioBandwidth failed:', e);
        return { cutoffHz: 0, atNyquist: false, fingerprint: null, confidence: 0, error: String(e && e.message || e) };
    }
}

var TWIDDLE_CACHE = {};
function twiddles(N){
    var c = TWIDDLE_CACHE[N];
    if (c) return c;
    var cos = new Float64Array(N / 2), sin = new Float64Array(N / 2);
    for (var i = 0; i < N / 2; i++){ var a = -2 * Math.PI * i / N; cos[i] = Math.cos(a); sin[i] = Math.sin(a); }
    c = { cos: cos, sin: sin };
    TWIDDLE_CACHE[N] = c;
    return c;
}

function computeMagnitude(data, fftSize){
    var N = fftSize, re = new Float64Array(N), im = new Float64Array(N);
    for (var i = 0; i < N; i++) re[i] = i < data.length ? data[i] * (0.5 - 0.5 * Math.cos(2 * Math.PI * i / N)) : 0;
    var bits = Math.log2(N) | 0;
    for (var i = 0; i < N; i++){
        var j = 0;
        for (var b = 0; b < bits; b++) j = (j << 1) | (i >> b & 1);
        if (j > i){ var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
    }
    // Twiddle factors cached per N: without this the trig runs N·log2(N) times
    // per FFT, and audioBandwidth now does five FFTs instead of one (measured
    // 2026-08-19: 309 ms → 78 ms per file for the same answer).
    var tw = twiddles(N);
    for (var s = 2; s <= N; s *= 2){
        var half = s / 2, step = N / s;
        for (var k = 0; k < N; k += s){
            for (var j2 = 0; j2 < half; j2++){
                var ti = j2 * step;
                var wr = tw.cos[ti], wi = tw.sin[ti];
                var tre = wr * re[k + j2 + half] - wi * im[k + j2 + half];
                var tim = wr * im[k + j2 + half] + wi * re[k + j2 + half];
                re[k + j2 + half] = re[k + j2] - tre; im[k + j2 + half] = im[k + j2] - tim;
                re[k + j2] += tre; im[k + j2] += tim;
            }
        }
    }
    var mag = new Float64Array(N / 2);
    for (var i = 0; i < N / 2; i++) mag[i] = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
    return mag;
}

/* ─────────────────────────────────────────────────────────────
   Public API
   ────────────────────────────────────────────────────────────── */
/* Bytes in, evidence out — no FileReader, so this is the form a worker (and a
   Node test) can call. scan(file) is the thin browser wrapper below. */
function scanBytes(head, tail){
    tail = tail || new Uint8Array(0);

    // (1) Structured tags. Vendor names are only evidence when a TOOL wrote
    //     them; the parser is what tells a TSSE frame from a song title.
    var reader = (typeof window !== 'undefined' && window.AudioTagReader)
        || (typeof self !== 'undefined' && self.AudioTagReader)
        || (typeof AudioTagReader !== 'undefined' ? AudioTagReader : null);
    var parsed = null, weakHit = null;
    if (reader && typeof reader.readBytes === 'function') {
        try { parsed = reader.readBytes(head, tail.length ? tail : head); } catch (_) {}
    }
    if (parsed && parsed.fields && parsed.fields.length) {
        var meta = scanTagFields(parsed.fields);
        if (meta && meta.detected) return meta;
        weakHit = meta;                           // name-only mention, carried below
    }

    // (2) C2PA manifests are a CONTAINER-level structure, not a tag field, so
    //     they are still matched over the raw bytes — but only on markers that
    //     cannot occur by accident (the bare 'jumbf' box name can, and is gone).
    var c2pa = scanC2PAMarkers(decodeUtf8(head) + '\n' + decodeUtf8(tail));
    if (c2pa) { c2pa.strength = 'declaration'; return c2pa; }

    // Nothing. A name-only mention travels as context with detected:false, so a
    // caller that only checks `.detected` behaves exactly as before.
    return weakHit || { detected: false };
}

async function scan(file, opts){
    opts = opts || {};
    var headBytes = opts.headBytes || 256 * 1024;   // 256 KB head
    var tailBytes = opts.tailBytes || 64 * 1024;    // 64  KB tail (ID3v1, APE, FLAC tail tags)
    var head = await readFirstChunk(file, headBytes);
    var tail = file.size > headBytes ? await readLastChunk(file, tailBytes) : head;
    return scanBytes(head, tail);
}

/* Display labels for detected sources. Kept here, not in the inline
   blade HTML, so AI-tool brand names don't appear in the page source
   (per user feedback 2026-05-07: marketing copy should be brand-agnostic
   so it doesn't age out as generators rename / shut down). When a
   watermark IS actually found in a user's file, we display the real
   label as a factual detection result. */
var SOURCE_LABELS = {
    stable_audio: 'Stability AI · Stable Audio',
    suno: 'Suno AI',
    udio: 'Udio',
    elevenlabs: 'ElevenLabs',
    musicgen: 'Meta · MusicGen / AudioCraft',
    riffusion: 'Riffusion',
    audioldm: 'AudioLDM',
    aiva: 'AIVA',
    boomy: 'Boomy',
    mubert: 'Mubert',
    soundraw: 'Soundraw',
    beatoven: 'Beatoven.ai',
    loudly: 'Loudly.com',
    soundverse: 'Soundverse',
    generic_ai: 'AI-generated (generic)',
    c2pa: 'C2PA Content Credentials'
};

/* Stub-leak guard: real impl always replaces a stub on second load. */
window.AudioWatermarkDetector = {
    scan: scan,
    scanBytes: scanBytes,
    audioBandwidth: audioBandwidth,
    // Back-compat: same measurement, but it never claims a fingerprint now.
    spectralFingerprint: audioBandwidth,
    SOURCE_LABELS: SOURCE_LABELS,
    version: '1.0.0',
    metadataPatternsCount: METADATA_PATTERNS.length,
    c2paMarkersCount: C2PA_MARKERS.length,
    __isStub: false
};

/* Also CommonJS-exported so the evidence rules are unit-tested in Node against
   real files (tests/ai-detector-evidence.test.mjs) — same pattern as
   audio-tag-writer.js. The browser path is unchanged. */
if (typeof module !== 'undefined' && module.exports) module.exports = window.AudioWatermarkDetector;

})();
